import React, { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import AutocompleteDropdown from './AutocompleteDropdown';
import UserService from '../../services/userService';

const DoctorAutocomplete = ({
  selectedDoctor,
  onDoctorSelect,
  placeholder = "Seleccione un doctor",
  searchPlaceholder = "Buscar doctor...",
  disabled = false,
  className = '',
  showLicense = true,
  minSearchLength = 2,
  maxResults = 10,
  ...props
}) => {
  const auth = useAuth();

  // Function to fetch doctors based on search term
  const fetchDoctors = useCallback(async (searchTerm, token) => {
    try {
      // If no search term, return empty array to avoid loading all doctors
      if (!searchTerm || searchTerm.length < minSearchLength) {
        return [];
      }

      const result = await UserService.getDoctors(token);
      const doctors = result.data || [];
      
      // Client-side filtering
      return doctors.filter(doctor => {
        const fullName = doctor.name.toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        
        return fullName.includes(searchLower) ||
               (doctor.email && doctor.email.toLowerCase().includes(searchLower)) ||
               (doctor.licenseId && doctor.licenseId.includes(searchTerm));
      });
    } catch (error) {
      console.error('Error fetching doctors:', error);
      throw new Error('Error al buscar doctores');
    }
  }, [minSearchLength]);

  // Function to display doctor text in the dropdown trigger
  const displayDoctor = useCallback((doctor) => {
    if (!doctor) return '';
    return doctor.name;
  }, []);

  // Function to get unique key for each doctor
  const getDoctorKey = useCallback((doctor) => {
    return doctor.id;
  }, []);

  // Custom render function for doctor items
  const renderDoctorItem = useCallback((doctor, isSelected, isHighlighted) => {
    return (
      <div className={`autocomplete-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}>
        <div className="doctor-info">
          <div className="doctor-name">
            {doctor.name}
          </div>
          <div className="doctor-details">
            {showLicense && doctor.licenseId && (
              <span className="doctor-license">
                Cédula: {doctor.licenseId}
              </span>
            )}
            {doctor.email && (
              <span className="doctor-email">
                {doctor.email}
              </span>
            )}
          </div>
        </div>
        {isSelected && (
          <i className="fas fa-check selected-icon"></i>
        )}
      </div>
    );
  }, [showLicense]);

  // Custom render function for selected doctor preview
  const renderSelectedPreview = useCallback((doctor) => {
    return (
      <div className="selected-doctor-preview">
        <span className="doctor-name">
          {doctor.name}
        </span>
        {showLicense && doctor.licenseId && (
          <span className="doctor-license">
            (Cédula: {doctor.licenseId})
          </span>
        )}
      </div>
    );
  }, [showLicense]);

  return (
    <AutocompleteDropdown
      fetchFunction={fetchDoctors}
      displayFunction={displayDoctor}
      keyFunction={getDoctorKey}
      selectedItem={selectedDoctor}
      onItemSelect={onDoctorSelect}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      disabled={disabled}
      icon="fas fa-user-md"
      maxResults={maxResults}
      minSearchLength={minSearchLength}
      debounceDelay={300}
      showClearButton={true}
      emptyMessage="No se encontraron doctores"
      loadingMessage="Buscando doctores..."
      renderCustomItem={renderDoctorItem}
      renderSelectedPreview={renderSelectedPreview}
      token={auth.user?.access_token}
      className={`doctor-autocomplete ${className}`}
      searchOnFocus={false}
      clearOnSelect={false}
      {...props}
    />
  );
};

export default DoctorAutocomplete;
